/**
 * @file     viewingRequestModel.ts
 * @purpose  Pure rules behind the viewing request sheet: the draft shape,
 *           field validation (mobile phone and the REALTOR® answer are
 *           required), suggested time slots, the POST body for
 *           /api/rentals/viewing-requests, and the preview rows. No React, so
 *           the sheet and its tests share one source.
 */
import { VIEWING_REQUEST_LIMITS, VIEWING_TIME_WINDOWS } from '@/core/config/viewingRequest';
import { formatWeekdayMonthDay } from '@/utils/dateFormat';
import { consentChoicesPayload, type ConsentPayload } from '@/components/lead-capture/consentPayload';
import { contactPayload, validateContact, type ContactErrors, type ContactValues } from '@/services/leads/contactFields';
import type { AntiSpamMetadata } from '@/services/anti-spam/useAntiSpam';
import { formatPhoneInput } from '@/utils/formatPhone';

export type ViewingWindow = (typeof VIEWING_TIME_WINDOWS)[number];
export interface ViewingSlot {
  date: string;
  window: ViewingWindow;
}

export interface ViewingDraft {
  contact: ContactValues;
  slots: ViewingSlot[];
  moveIn: string;
  realtor: boolean | null;
  note: string;
  followupOptIn: boolean;
  smsOptIn: boolean;
  emailOptIn: boolean;
}

/** Who receives the request. MLS: the listing brokerage. Owner: Ray. */
export interface ViewingTarget {
  recipient: 'mls' | 'owner';
  listingKey: string | null;
  ownerListingId: string | null;
}

export interface ViewingErrors {
  contact: ContactErrors;
  realtor: boolean;
  note: boolean;
}

type Translate = Parameters<typeof validateContact>[1];

type AccountUser = { email?: string | null; user_metadata?: Record<string, unknown> | null } | null;

/** A signed-in renter starts with what the account already holds. */
export function contactFromUser(user: AccountUser): ContactValues {
  if (!user) return { firstName: '', lastName: '', email: '', phone: '' };
  const meta = (user.user_metadata ?? {}) as { first_name?: string; last_name?: string; name?: string; phone?: string };
  const [first = '', ...rest] = (meta.name ?? '').trim().split(/\s+/);
  return {
    firstName: meta.first_name ?? first,
    lastName: meta.last_name ?? rest.join(' '),
    email: user.email ?? '',
    phone: meta.phone ? formatPhoneInput(meta.phone) : '',
  };
}

export function emptyDraft(contact: ContactValues, moveIn = ''): ViewingDraft {
  return { contact, slots: [], moveIn, realtor: null, note: '', followupOptIn: false, smsOptIn: false, emailOptIn: false };
}

export function validateDraft(draft: ViewingDraft, t: Translate): ViewingErrors {
  return {
    contact: validateContact(draft.contact, t),
    realtor: draft.realtor === null,
    note: draft.note.length > VIEWING_REQUEST_LIMITS.noteMax,
  };
}

export function hasErrors(errors: ViewingErrors): boolean {
  return Object.keys(errors.contact).length > 0 || errors.realtor || errors.note;
}

export const slotKey = (s: ViewingSlot) => `${s.date}:${s.window}`;

export function toggleSlot(slots: ViewingSlot[], slot: ViewingSlot): ViewingSlot[] {
  const key = slotKey(slot);
  if (slots.some((s) => slotKey(s) === key)) return slots.filter((s) => slotKey(s) !== key);
  if (slots.length >= VIEWING_REQUEST_LIMITS.timesMax) return slots;
  return [...slots, slot].sort((a, b) => slotKey(a).localeCompare(slotKey(b)));
}

const iso = (d: Date) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

/** YYYY-MM-DD plus n days, in calendar arithmetic (no timezone drift). */
export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return iso(new Date(Date.UTC(y!, m! - 1, d! + n)));
}

/** 0 = Sunday. */
export function weekday(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay();
}

/**
 * Three suggestions from tomorrow on: the next weekday evening, then the next
 * Saturday morning and Sunday afternoon (the windows renters ask for most).
 */
export function suggestSlots(today: string): ViewingSlot[] {
  const out: ViewingSlot[] = [];
  for (let i = 1; i <= 7 && out.length < 1; i += 1) {
    const day = addDays(today, i);
    const wd = weekday(day);
    if (wd >= 1 && wd <= 5) out.push({ date: day, window: 'evening' });
  }
  for (let i = 1; i <= 7; i += 1) {
    const day = addDays(today, i);
    if (weekday(day) === 6) out.push({ date: day, window: 'morning' });
    if (weekday(day) === 0) out.push({ date: day, window: 'afternoon' });
  }
  return out.slice(0, 3).sort((a, b) => a.date.localeCompare(b.date));
}

/** "Thu, Oct 15" from a YYYY-MM-DD, fixed to UTC so server and client agree. */
export function slotDayLabel(date: string): string {
  return formatWeekdayMonthDay(date);
}

export interface ViewingRequestBody extends ConsentPayload {
  listingSource: 'mls' | 'owner';
  listingKey?: string;
  ownerListingId?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  preferredTimes: ViewingSlot[];
  moveIn: string | null;
  workingWithRealtor: boolean;
  note: string | null;
  followupOptIn: boolean;
  antiSpam: AntiSpamMetadata;
  submissionId: string;
  sourcePage?: string;
}

/** The body the route's viewingRequestInputSchema parses. Call only on a draft that validated. */
export function buildRequestBody(
  draft: ViewingDraft,
  target: ViewingTarget,
  extra: { antiSpam: AntiSpamMetadata; submissionId: string; sourcePage?: string },
): ViewingRequestBody {
  const c = contactPayload(draft.contact);
  return {
    listingSource: target.recipient,
    ...(target.recipient === 'mls' ? { listingKey: target.listingKey! } : { ownerListingId: target.ownerListingId! }),
    firstName: c.firstName,
    lastName: c.lastName,
    email: c.email,
    phone: c.phone,
    preferredTimes: draft.slots,
    moveIn: draft.moveIn || null,
    workingWithRealtor: draft.realtor === true,
    note: draft.note.trim() || null,
    followupOptIn: draft.followupOptIn,
    ...consentChoicesPayload({ email: draft.emailOptIn, sms: draft.smsOptIn }, c.phone),
    antiSpam: extra.antiSpam,
    submissionId: extra.submissionId,
    ...(extra.sourcePage ? { sourcePage: extra.sourcePage } : {}),
  };
}

/** How a failed POST reads to the renter. The draft is always kept. */
export type SubmitFailure = 'offline' | 'unavailable' | 'rate_limited' | 'failed';

export function failureFor(status: number | null, online: boolean): SubmitFailure {
  if (!online) return 'offline';
  if (status === 404) return 'unavailable';
  if (status === 429) return 'rate_limited';
  return 'failed';
}

export const DRAFT_STORAGE_PREFIX = 'relta:viewing-draft:';
